import { useState, useEffect, useMemo } from "react";
import { ArrowRight, Package, Zap, Diamond, Flag, MessageSquare, Trash2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getPlayerChangelog, resolveItems, getPlayerNotes, createPlayerNote, deletePlayerNote, getUpgradeFlags } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";

const WOWHEAD_BASE = "https://www.wowhead.com/tbc";

function refreshWowheadTooltips() {
  try {
    if (window.$WowheadPower && window.$WowheadPower.refreshLinks) {
      window.$WowheadPower.refreshLinks();
    }
  } catch (e) { /* silent */ }
}

const TYPE_ICONS = { item: Package, enchant: Zap, gem: Diamond };
const TYPE_COLORS = { item: "text-purple-400", enchant: "text-green-400", gem: "text-blue-400" };

export default function ChangelogTab({ playerId }) {
  const [changelog, setChangelog] = useState([]);
  const [notes, setNotes] = useState([]);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resolved, setResolved] = useState({ items: {}, enchants: {}, gems: {} });
  const [newNote, setNewNote] = useState("");
  const { canWrite } = useAuth();

  useEffect(() => {
    if (!playerId) return;
    setLoading(true);
    Promise.all([
      getPlayerChangelog(playerId),
      getPlayerNotes(playerId).catch(() => []),
      getUpgradeFlags(playerId).catch(() => []),
    ]).then(([clData, notesData, flagsData]) => {
      const cl = clData.changelog || [];
      setChangelog(cl);
      setNotes(notesData);
      setFlags(flagsData);

      // Collect IDs for resolution
      const itemIds = new Set();
      const enchantIds = new Set();
      const gemIds = new Set();
      for (const entry of cl) {
        for (const ch of entry.changes) {
          if (ch.type === "item") { if (ch.from_id) itemIds.add(ch.from_id); if (ch.to_id) itemIds.add(ch.to_id); }
          else if (ch.type === "enchant") { if (ch.from_id) enchantIds.add(ch.from_id); if (ch.to_id) enchantIds.add(ch.to_id); if (ch.item_id) itemIds.add(ch.item_id); }
          else if (ch.type === "gem") { if (ch.from_id) gemIds.add(ch.from_id); if (ch.to_id) gemIds.add(ch.to_id); if (ch.item_id) itemIds.add(ch.item_id); }
        }
      }
      if (itemIds.size > 0 || enchantIds.size > 0 || gemIds.size > 0) {
        resolveItems([...itemIds], [...enchantIds], [...gemIds])
          .then((res) => { setResolved(res); setTimeout(refreshWowheadTooltips, 500); })
          .catch(() => {});
      }
    }).catch(() => setChangelog([]))
      .finally(() => setLoading(false));
  }, [playerId]);

  useEffect(() => {
    if (!loading && changelog.length > 0) {
      setTimeout(refreshWowheadTooltips, 300);
      setTimeout(refreshWowheadTooltips, 1500);
    }
  }, [loading, changelog, resolved]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    const now = new Date();
    const day = now.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    const weekStart = monday.toISOString().split("T")[0];
    try {
      const note = await createPlayerNote(playerId, newNote.trim(), weekStart);
      setNotes((prev) => [note, ...prev]);
      setNewNote("");
      toast.success("Note added");
    } catch (e) {
      const status = e.response?.status;
      toast.error(status === 401 ? "Sign in to add notes" : "Failed to add note");
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await deletePlayerNote(noteId);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Note removed");
    } catch { toast.error("Failed to remove"); }
  };

  if (loading) {
    return (
      <div className="py-8 text-center" data-testid="changelog-loading">
        <div className="h-6 w-6 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin mx-auto" />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-4" data-testid="changelog-tab">
      {/* Active Flags */}
      {flags.length > 0 && (
        <div className="p-3 rounded-sm border border-orange-500/20 bg-orange-950/10">
          <div className="flex items-center gap-2 mb-2">
            <Flag className="h-3.5 w-3.5 text-orange-400" />
            <span className="text-xs font-mono uppercase tracking-wider text-orange-400">Active Upgrade Flags ({flags.length})</span>
          </div>
          <div className="space-y-1">
            {flags.map((f) => (
              <div key={f.id} className="flex items-center gap-2 text-sm">
                <span className="text-xs font-mono text-muted-foreground w-20">{f.slot}</span>
                <span className={`text-xs ${TYPE_COLORS[f.flag_type] || ""}`}>{f.flag_type}</span>
                <span className="text-foreground/80">{f.flagged_name || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="border border-border/20 rounded-sm overflow-hidden" data-testid="notes-section">
        <div className="px-4 py-2 bg-card/30 border-b border-border/10 flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5 text-yellow-400" />
          <span className="text-xs font-mono uppercase tracking-wider text-yellow-400">Officer Notes</span>
          <span className="text-[10px] text-muted-foreground ml-auto">{notes.length} note(s)</span>
        </div>
        {canWrite && (
          <div className="px-4 py-2 flex gap-2 border-b border-border/10">
            <input
              data-testid="note-input"
              type="text"
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a note for this week..."
              className="flex-1 h-7 px-2 text-sm bg-background/50 border border-border/30 rounded-sm focus:border-yellow-500/50 focus:outline-none placeholder:text-muted-foreground/50"
              onKeyDown={(e) => e.key === "Enter" && handleAddNote()}
            />
            <Button size="sm" onClick={handleAddNote} disabled={!newNote.trim()}
              className="h-7 text-xs rounded-sm gap-1 bg-yellow-600 hover:bg-yellow-700 text-white">
              <Plus className="h-3 w-3" /> Add
            </Button>
          </div>
        )}
        {notes.length === 0 ? (
          <div className="px-4 py-4 text-center text-xs text-muted-foreground">No notes yet</div>
        ) : (
          <div className="divide-y divide-border/10">
            {notes.map((n) => (
              <div key={n.id} className="px-4 py-2 flex items-start gap-2 group" data-testid={`note-${n.id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm">{n.note}</p>
                  <div className="flex items-center gap-2 text-[10px] text-muted-foreground mt-0.5">
                    <span>{n.author}</span>
                    {n.week_start && <span>Week of {n.week_start}</span>}
                    <span>{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                {canWrite && (
                  <button onClick={() => handleDeleteNote(n.id)}
                    className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 h-5 w-5 flex items-center justify-center rounded-sm hover:bg-destructive/20">
                    <Trash2 className="h-3 w-3 text-muted-foreground hover:text-destructive" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Gear Changelog */}
      {changelog.length === 0 ? (
        <div className="py-8 text-center text-muted-foreground" data-testid="changelog-empty">
          <Package className="h-8 w-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No gear changes detected across raids.</p>
        </div>
      ) : (
        changelog.map((entry, i) => (
          <div key={i} className="border border-border/20 rounded-sm overflow-hidden" data-testid={`changelog-entry-${i}`}>
            <div className="px-4 py-2 bg-card/30 border-b border-border/10 flex items-center justify-between">
              <div>
                <span className="text-sm font-medium">{entry.raid.raid_name}</span>
                <span className="text-xs text-muted-foreground font-mono ml-2">{entry.raid.date}</span>
              </div>
              <span className="text-[10px] font-mono text-muted-foreground">
                {entry.changes.length} change{entry.changes.length !== 1 ? "s" : ""}
              </span>
            </div>
            <div className="divide-y divide-border/10">
              {entry.changes.map((ch, ci) => (
                <ChangeRow key={ci} ch={ch} index={`${i}-${ci}`} resolved={resolved} />
              ))}
            </div>
          </div>
        ))
      )}
    </div>
  );
}

function ChangeRow({ ch, index, resolved }) {
  const Icon = TYPE_ICONS[ch.type] || Package;
  const color = TYPE_COLORS[ch.type] || "text-foreground";
  const label = ch.type === "gem" ? `Gem #${ch.gem_slot || 1}` : ch.type.charAt(0).toUpperCase() + ch.type.slice(1);

  return (
    <div className="px-4 py-2.5 flex items-center gap-3 hover:bg-card/20 transition-colors"
      data-testid={`changelog-change-${index}`}>
      <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${color}`} />
      <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground w-20 flex-shrink-0">
        {ch.slot}
      </span>
      <span className={`text-[10px] font-mono uppercase tracking-wider ${color} w-14 flex-shrink-0`}>
        {label}
      </span>
      <div className="flex items-center gap-2 min-w-0 flex-1 flex-wrap">
        <ResolvedLink ch={ch} which="from" resolved={resolved} dim />
        <ArrowRight className="h-3 w-3 text-muted-foreground/50 flex-shrink-0" />
        <ResolvedLink ch={ch} which="to" resolved={resolved} />
      </div>
    </div>
  );
}

function ResolvedLink({ ch, which, resolved, dim }) {
  const id = which === "from" ? ch.from_id : ch.to_id;
  const baseClass = dim ? "opacity-60" : "font-medium";

  if (!id || id === 0) {
    return <span className={`text-sm text-muted-foreground ${baseClass}`}>—</span>;
  }

  if (ch.type === "item") {
    const info = resolved.items?.[String(id)];
    const name = info?.name || `Item #${id}`;
    const itemColor = info?.color || (dim ? "#888" : "#a335ee");
    return (
      <div className="flex items-center gap-1.5">
        {info?.icon && <img src={info.icon} alt="" className="w-5 h-5 rounded-[2px] border border-border/50" style={{ borderColor: itemColor }} loading="lazy" />}
        <a href={`${WOWHEAD_BASE}/item=${id}`} onClick={(e) => e.preventDefault()}
          className={`text-sm hover:underline truncate ${baseClass}`}
          style={{ color: itemColor }}>
          {name}
        </a>
      </div>
    );
  }

  if (ch.type === "enchant") {
    const info = resolved.enchants?.[String(id)];
    const name = info?.name || `Enchant #${id}`;
    const itemId = ch.item_id || 0;
    const url = itemId > 0 ? `${WOWHEAD_BASE}/item=${itemId}&ench=${id}` : `${WOWHEAD_BASE}/spell=${id}`;
    return (
      <a href={url} onClick={(e) => e.preventDefault()}
        className={`text-sm hover:underline truncate text-green-400 ${baseClass}`}>
        {name}
      </a>
    );
  }

  if (ch.type === "gem") {
    const info = resolved.gems?.[String(id)];
    const name = info?.name || `Gem #${id}`;
    const gemColor = info?.color || "#1eff00";
    return (
      <div className="flex items-center gap-1.5">
        {info?.icon && <img src={info.icon} alt="" className="w-4 h-4 rounded-[2px] border border-border/50" loading="lazy" />}
        <a href={`${WOWHEAD_BASE}/item=${id}`} onClick={(e) => e.preventDefault()}
          className={`text-sm hover:underline truncate ${baseClass}`}
          style={{ color: gemColor }}>
          {name}
        </a>
      </div>
    );
  }

  return <span className={`text-sm ${baseClass}`}>#{id}</span>;
}
